import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Shield, Plus, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffDiscipline() {
  const [records, setRecords] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ staff_id: '', staff_name: '', type: 'verbal_warning', reason: '', details: '', action_taken: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchRecords(); fetchCaregivers(); }, []);
  const fetchRecords = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API_URL}/api/admin/discipline`, { withCredentials: true }); setRecords(data); }
    catch { toast.error('Failed to load records'); }
    setLoading(false);
  };
  const fetchCaregivers = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/admin/caregivers`, { withCredentials: true }); setCaregivers(data); }
    catch { /* silent */ }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/discipline`, form, { withCredentials: true });
      toast.success('Record created');
      setShowCreate(false);
      setForm({ staff_id: '', staff_name: '', type: 'verbal_warning', reason: '', details: '', action_taken: '' });
      fetchRecords();
    } catch { toast.error('Failed to create record'); }
    setCreating(false);
  };

  const typeLabels = { verbal_warning: 'Verbal Warning', written_warning: 'Written Warning', final_warning: 'Final Warning', suspension: 'Suspension', dismissal: 'Dismissal' };
  const typeColors = { verbal_warning: 'bg-amber-500/15 text-amber-400', written_warning: 'bg-[#D97757]/15 text-[#D97757]', final_warning: 'bg-red-500/15 text-red-400', suspension: 'bg-red-500/15 text-red-400', dismissal: 'bg-red-600/15 text-red-500' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-discipline-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Discipline Records</h1>
            <p className="text-muted-foreground mt-1">{records.length} record(s)</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2"><Plus className="w-4 h-4" />New Record</Button>
            </DialogTrigger>
            <DialogContent className="bg-background border-border max-w-md">
              <DialogHeader><DialogTitle className="text-xl font-heading text-foreground">New Discipline Record</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label className="text-foreground">Staff Member</Label>
                  <Select value={form.staff_id} onValueChange={(v) => { const cg = caregivers.find(c => c.id === v); setForm(p => ({ ...p, staff_id: v, staff_name: cg?.name || '' })); }}>
                    <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue placeholder="Select staff..." /></SelectTrigger>
                    <SelectContent>{caregivers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
                    <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label className="text-foreground">Reason</Label><Input value={form.reason} onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))} className="bg-muted border-border text-foreground" required /></div>
                <div className="space-y-2"><Label className="text-foreground">Details</Label><Input value={form.details} onChange={(e) => setForm(p => ({ ...p, details: e.target.value }))} className="bg-muted border-border text-foreground" /></div>
                <div className="space-y-2"><Label className="text-foreground">Action Taken</Label><Input value={form.action_taken} onChange={(e) => setForm(p => ({ ...p, action_taken: e.target.value }))} className="bg-muted border-border text-foreground" /></div>
                <Button type="submit" disabled={creating} className="w-full btn-primary">{creating ? 'Creating...' : 'Create Record'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : records.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-xl border border-border">
            <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground text-lg">No discipline records</p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.map((r, i) => (
              <Card key={i} className="glass-card border-border" data-testid={`discipline-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                      <span className="text-foreground font-medium">{r.staff_name}</span>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${typeColors[r.type] || typeColors.verbal_warning}`}>{typeLabels[r.type] || r.type}</span>
                  </div>
                  <p className="text-sm text-foreground"><span className="text-muted-foreground">Reason:</span> {r.reason}</p>
                  {r.action_taken && <p className="text-sm text-muted-foreground mt-1">Action: {r.action_taken}</p>}
                  <p className="text-xs text-muted-foreground mt-2">By {r.created_by_name} | {r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A'}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
